const EXPECTED_SHELL_REVISION = "phase0-shell-v5-title-cache-guard";
const EXPECTED_APP_TITLE = "我的听戏";
const LEGACY_APP_TITLE = "爸爸的听戏";
const SHELL_CACHE_PREFIX = "voice-companion-shell-";
const SW_STATUS = "SW_STATUS";
const SW_ROLLBACK_BAD_VERSION = "SW_ROLLBACK_BAD_VERSION";

type ServiceWorkerStatus = {
  type?: string;
  revision?: string;
};

async function postServiceWorkerMessage<T>(
  controller: ServiceWorker,
  message: Record<string, unknown>,
  timeoutMs = 1_500,
): Promise<T | null> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(() => resolve(null), timeoutMs);
    const channel = new MessageChannel();
    channel.port1.onmessage = (event) => {
      window.clearTimeout(timeout);
      resolve(event.data as T);
    };
    try {
      controller.postMessage(message, [channel.port2]);
    } catch {
      window.clearTimeout(timeout);
      resolve(null);
    }
  });
}

async function deleteShellCaches(): Promise<void> {
  if (!("caches" in window)) {
    return;
  }

  const cacheNames = await caches.keys();
  await Promise.all(
    cacheNames
      .filter((cacheName) => cacheName.startsWith(SHELL_CACHE_PREFIX))
      .map((cacheName) => caches.delete(cacheName)),
  );
}

function reloadOnceAfterRecovery(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get("sw-refresh") === EXPECTED_SHELL_REVISION) {
    return;
  }

  url.searchParams.set("sw-refresh", EXPECTED_SHELL_REVISION);
  url.searchParams.set("cache-reset", String(Date.now()));
  window.location.replace(url.toString());
}

function clearRecoveryMarker(): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get("sw-refresh") !== EXPECTED_SHELL_REVISION) {
    return;
  }

  url.searchParams.delete("sw-refresh");
  url.searchParams.delete("cache-reset");
  window.history.replaceState(null, "", url.toString());
}

async function cachedTextContainsLegacyTitle(path: string): Promise<boolean> {
  if (!("caches" in window)) {
    return false;
  }

  const response = await caches.match(path);
  if (!response) {
    return false;
  }

  try {
    const text = await response.clone().text();
    return text.includes(LEGACY_APP_TITLE);
  } catch {
    return false;
  }
}

async function staleTitleReason(): Promise<string | null> {
  const appleTitle = document
    .querySelector('meta[name="apple-mobile-web-app-title"]')
    ?.getAttribute("content");

  if (
    document.title.includes(LEGACY_APP_TITLE) ||
    appleTitle?.includes(LEGACY_APP_TITLE)
  ) {
    return "legacy-app-title:document";
  }

  if (await cachedTextContainsLegacyTitle("/")) {
    return "legacy-app-title:cached-root";
  }

  if (await cachedTextContainsLegacyTitle("/manifest.webmanifest")) {
    return "legacy-app-title:cached-manifest";
  }

  if (
    document.title &&
    !document.title.includes(EXPECTED_APP_TITLE) &&
    !window.location.pathname.startsWith("/reset-pwa-cache")
  ) {
    return "missing-expected-app-title";
  }

  return null;
}

async function recoverStaleController(
  registration: ServiceWorkerRegistration,
  reason: string,
): Promise<void> {
  const controller = navigator.serviceWorker.controller;

  if (controller) {
    await postServiceWorkerMessage(controller, {
      type: SW_ROLLBACK_BAD_VERSION,
      reason,
    });
  }

  await deleteShellCaches();
  await registration.unregister();
  reloadOnceAfterRecovery();
}

async function ensureFreshController(
  registration: ServiceWorkerRegistration,
): Promise<void> {
  const titleReason = await staleTitleReason();
  if (titleReason) {
    await recoverStaleController(registration, titleReason);
    return;
  }

  const controller = navigator.serviceWorker.controller;
  if (!controller) {
    return;
  }

  const status = await postServiceWorkerMessage<ServiceWorkerStatus>(
    controller,
    { type: SW_STATUS },
  );
  if (status?.revision === EXPECTED_SHELL_REVISION) {
    clearRecoveryMarker();
    return;
  }

  await recoverStaleController(
    registration,
    status?.revision
      ? `stale-shell-revision:${status.revision}`
      : "missing-shell-revision",
  );
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator) || import.meta.env.DEV) {
    return;
  }

  const hadControllerAtStart = Boolean(navigator.serviceWorker.controller);
  let reloadedForNewController = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadControllerAtStart) {
      return;
    }

    if (reloadedForNewController) {
      return;
    }

    reloadedForNewController = true;
    reloadOnceAfterRecovery();
  });

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then(async (registration) => {
        await registration.update().catch(() => undefined);
        await ensureFreshController(registration);
      })
      .catch(() => {
        // The app shell still works online; Phase 0A records SW install separately. [TRACE: SW-001]
      });
  });
}
